import React from 'react';
import type {ReactElement} from 'react';

import type {IntlShape} from 'react-intl';

import {ExternalLinkIcon, QrCodeIcon} from '@edume/magnificent';
import type {ContentShape} from '@edume/magnificent';

import {MEDIA_LIBRARY_TABS} from '../useMediaLibraryTab';
import type {
  LibraryTab,
  LibraryTabState,
  LibraryTabStateSetter,
} from '../useMediaLibraryTab';

type LibraryStepType = 'imageOnly' | 'videoOnly' | 'both';

interface SidebarConfigArgs {
  type: LibraryStepType;
  libraryTab: LibraryTabState;
  setLibraryTab: LibraryTabStateSetter;
  intl: IntlShape;
}

const assertUnreachable = (x: never): never => {
  throw new Error(`Unhandled case: ${JSON.stringify(x)}`);
};

const getSidebarConfig = ({
  type,
  libraryTab,
  setLibraryTab,
  intl,
}: SidebarConfigArgs): NonNullable<ContentShape['sidebarConfig']> => {
  const getLibrarySidebarTabConfig = (tab: LibraryTab) => ({
    active: libraryTab === tab,
    onClick: () => setLibraryTab(tab),
  });

  const getImageLibrarySidebarConfigBase = () => ({
    title: intl.formatMessage({id: 'ImageLibrary.tab.title'}),
    options: [
      {
        text: intl.formatMessage({id: 'ImageLibrary.tab.group'}),
        iconUrl: '/resources/img/24_monitor.svg',
        ...getLibrarySidebarTabConfig(MEDIA_LIBRARY_TABS.GROUP_IMAGES),
      },
      {
        text: intl.formatMessage({id: 'ImageLibrary.tab.edume'}),
        iconUrl: '/resources/img/24_box.svg',
        ...getLibrarySidebarTabConfig(MEDIA_LIBRARY_TABS.EDUME_IMAGES),
        dataAutomation: 'edume-library-option',
      },
    ],
  });

  const getVideoLibrarySidebarConfigSections = () => [
    {
      title: intl.formatMessage({
        id: 'VideoLibrary.tabSection.library',
      }),
      options: [
        {
          text: intl.formatMessage({id: 'VideoLibrary.tab.group'}),
          iconUrl: '/resources/img/24_monitor.svg',
          ...getLibrarySidebarTabConfig(MEDIA_LIBRARY_TABS.GROUP_VIDEOS),
        },
        {
          text: intl.formatMessage({
            id: 'VideoLibrary.tab.submitted',
          }),
          icon: <ExternalLinkIcon colour='white' />,
          dataAutomation: 'submitted-videos-tab',
          ...getLibrarySidebarTabConfig(MEDIA_LIBRARY_TABS.EXTERNAL_VIDEOS),
        },
      ],
    },
    {
      title: intl.formatMessage({
        id: 'VideoLibrary.tabSection.uploadTools',
      }),
      options: [
        {
          text: intl.formatMessage({
            id: 'VideoLibrary.tab.mediaUpload',
          }),
          icon: <QrCodeIcon colour='white' />,
          dataAutomation: 'media-upload-tab',
          ...getLibrarySidebarTabConfig(MEDIA_LIBRARY_TABS.UPLOAD_TOOLS),
        },
      ],
    },
  ];

  switch (type) {
    case 'imageOnly':
      return {
        ...getImageLibrarySidebarConfigBase(),
        bottomContent: {
          text: intl.formatMessage({id: 'ImageLibrary.tab.unsplash'}),
          linkText: intl.formatMessage({id: 'ImageLibrary.unsplashLink'}),
          linkHref: 'https://unsplash.com/',
          typeOfLink: '',
        },
      };
    case 'videoOnly':
      return {sections: getVideoLibrarySidebarConfigSections()};
    case 'both':
      return {
        sections: [
          getImageLibrarySidebarConfigBase(),
          ...getVideoLibrarySidebarConfigSections(),
        ],
      };
    default:
      return assertUnreachable(type);
  }
};

const getLibraryTitle = (type: LibraryTab, intl: IntlShape) => {
  switch (type) {
    case MEDIA_LIBRARY_TABS.GROUP_IMAGES:
    case MEDIA_LIBRARY_TABS.EDUME_IMAGES:
      return intl.formatMessage({id: 'UploadModal.title'}, {type: 'image'});
    case MEDIA_LIBRARY_TABS.GROUP_VIDEOS:
    case MEDIA_LIBRARY_TABS.EXTERNAL_VIDEOS:
      return intl.formatMessage({id: 'UploadModal.title'}, {type: 'video'});
    case MEDIA_LIBRARY_TABS.UPLOAD_TOOLS:
      return intl.formatMessage({id: 'VideoLibrary.tab.mediaUpload'});
    default:
      return assertUnreachable(type);
  }
};

type LibraryBodyOptions =
  | {
      videoLibrary: ReactElement;
      uploadLink: ReactElement;
      imageLibrary?: ReactElement;
    }
  | {
      imageLibrary: ReactElement;
      uploadLink?: undefined;
      videoLibrary?: undefined;
    };

const getLibraryType = ({
  imageLibrary,
  videoLibrary,
  uploadLink,
}: LibraryBodyOptions) => {
  if (imageLibrary) {
    if (videoLibrary && uploadLink) {
      return 'both';
    }
    return 'imageOnly';
  }
  return 'videoOnly';
};

type LibraryBodyProps = {
  libraryTab: LibraryTab;
  videoLibrary?: ReactElement;
  uploadLink?: ReactElement;
  imageLibrary?: ReactElement;
};

const LibraryBody = ({
  libraryTab,
  imageLibrary,
  videoLibrary,
  uploadLink,
}: LibraryBodyProps) => {
  switch (libraryTab) {
    case MEDIA_LIBRARY_TABS.GROUP_IMAGES:
    case MEDIA_LIBRARY_TABS.EDUME_IMAGES:
      return imageLibrary;
    case MEDIA_LIBRARY_TABS.GROUP_VIDEOS:
    case MEDIA_LIBRARY_TABS.EXTERNAL_VIDEOS:
      return videoLibrary;
    case MEDIA_LIBRARY_TABS.UPLOAD_TOOLS:
      return uploadLink;
    default:
      return assertUnreachable(libraryTab);
  }
};

type LibraryStepArgs = {
  libraryTab: LibraryTabState;
  setLibraryTab: LibraryTabStateSetter;
  onClose: () => void;
  intl: IntlShape;
} & LibraryBodyOptions;

export const getLibraryStep = ({
  libraryTab,
  setLibraryTab,
  onClose,
  intl,
  ...bodyRest
}: LibraryStepArgs) => {
  const type = getLibraryType(bodyRest);
  const {imageLibrary, videoLibrary, uploadLink} = bodyRest;
  return {
    title: (
      <div style={{paddingLeft: 24}}>{getLibraryTitle(libraryTab, intl)}</div>
    ),
    buttons: [
      {
        type: 'secondary',
        onClick: onClose,
        text: intl.formatMessage({id: 'Button.cancel'}),
        dataAutomation: 'cancel-button',
      },
    ],
    sidebarConfig: getSidebarConfig({type, libraryTab, setLibraryTab, intl}),
    body: (
      <LibraryBody
        libraryTab={libraryTab}
        imageLibrary={imageLibrary}
        videoLibrary={videoLibrary}
        uploadLink={uploadLink}
      />
    ),
  } satisfies ContentShape;
};
